import { NgZone } from '@angular/core';
import { Reducer } from 'redux';
import { tap } from 'rxjs/operators';

import store, { epicDependencies, storeObserver } from './store';
import { createFilteredReducer, PayloadAction } from './store.model';

function _runState<TState>(that: any, state: TState): void {
    const prevState = that.state;
    that.state = state;
    if (that._didPropsToDispatch !== true) {
        that._didPropsToDispatch = true;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        that.propsToDispatch && that.propsToDispatch();
    } else {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        that.stateToProps && that.stateToProps(prevState);
    }
}

function _bindStore<TState>(
    that: any,
    mapper?: (state: any) => TState,
    path?: string,
    ngZone?: NgZone,
): void {
    const nextMapper = mapper || ((state) => pluck<TState>(state, path));
    that._storeSub = storeObserver<any, TState>(store, nextMapper) // TODO revise store import
        .pipe(
            tap((state) => {
                if (ngZone) {
                    ngZone.run(() => _runState(that, state));
                } else {
                    _runState(that, state);
                }
            }),
        )
        .subscribe();
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useStore<TState>(
    that: any,
    path: string,
    reducer?: Reducer,
): void {
    that._storePath = path;

    if (reducer) {
        const reducerManager = (store as any).reducerManager;
        reducerManager.add(
            path,
            createFilteredReducer(
                reducer,
                (action) => action.storePath === that._storePath,
            ),
        );
        store.replaceReducer(reducerManager.reduce);
    }

    const ngZone = epicDependencies.injector.get<NgZone>(NgZone); // TODO revise
    _bindStore(that, null, path, ngZone);
}

export function useDispatcher<TActionType, TPayload>(
    that: any,
    action: PayloadAction<TActionType, TPayload>,
): void {
    action.storePath = that._storePath;
    store.dispatch(action);
}

export function disuseStore(that: any): void {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    that._storeSub && that._storeSub.unsubscribe();
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function UseStore<TState>(path: string, reducer?: Reducer) {
    return function <T extends new (...args: any[]) => {}>(ctr: T) {
        const origNgOnInit = ctr.prototype.ngOnInit;
        ctr.prototype.ngOnInit = function () {
            useStore(this, path, reducer);
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            origNgOnInit && origNgOnInit.apply(this);
        };

        const origNgOnDestroy = ctr.prototype.ngOnDestroy;
        ctr.prototype.ngOnDestroy = function () {
            disuseStore(this);
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            origNgOnDestroy && origNgOnDestroy.apply(this);
        };
    };
}

export const pluck = <T>(obj: any, path: string): T => {
    const arrPath = path.split('.');
    return arrPath.reduce((p: any, c) => {
        if (!p) return p;
        if (c.endsWith('[]')) {
            const k = c.replace('[]', '');
            return p.reduce((pp, cc) => {
                pp.push(...cc[k]);
                return pp;
            }, []);
        }
        return p[c];
    }, obj);
};
